import { useState, useCallback } from "react";
import { FormTemplate } from "../types/forms";
import { saveForm } from "../services/consultationform";
import { useToast } from "./useToast";

export function useTemplateSave(
  template: FormTemplate,
  onUpdateTemplate: (updatedTemplate: FormTemplate) => void
) {
  const [isSaving, setIsSaving] = useState(false);
  const showToast = useToast();
  // kept for compatibility if other components read it, but ideally should remove
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" | "" }>({
    text: "",
    type: "",
  });

  const saveTemplate = useCallback(
    async (finalTemplate: FormTemplate) => {
      if (
        !finalTemplate.title?.trim() ||
        !finalTemplate.description?.trim() ||
        !finalTemplate.sections?.length
      ) {
        showToast.error("Please fill all required fields before saving!");
        return;
      }

      if (!finalTemplate.uuid) {
        showToast.error("Form UUID is missing. Please create the form first.");
        console.error("Cannot save form: UUID is missing");
        return;
      }

      setIsSaving(true);

      try {
        // Map sections and fields to the expected API structure
        const sections = finalTemplate.sections.map((section, index) => {
          const metadataLabels = ["Question Text", "Answer Type", "Required Field", "Help Text"];
          const isQuestionSection = section.type === "question" || section.form_template_id === 4;

          // Filter out configuration metadata fields to only send real data fields
          const actualFields = (section.fields || []).filter(f => {
            if (!isQuestionSection) return true;
            return !metadataLabels.includes(f.label);
          });

          return {
            id: section.id,
            title: section.title || "",
            description: section.content || "",
            type: section.type || "textarea",
            order: index + 1,
            fields: actualFields.length > 0
              ? actualFields.map((field) => ({
                id: field.id,
                label: field.label || section.title || "",
                type: field.type || "textarea",
                options: Array.isArray(field.options)
                  ? field.options
                  : { content: field.options?.content ?? field.content ?? "" },
                required: field.required || false,
              }))
              : [
                {
                  id: `f${Date.now()}`,
                  label: section.title || "",
                  type: section.type || "textarea",
                  options: { content: section.content || "" },
                  required: false,
                },
              ],
          };
        });

        const dataToSend = {
          uuid: finalTemplate.uuid,
          title: finalTemplate.title,
          description: finalTemplate.description,
          // frequency: finalTemplate.frequency || "",
          status: finalTemplate.draft ? "draft" : "published",
          // auto_send: finalTemplate.auto_send || false,
          // company_id: finalTemplate.company_id || 0,
          sections,
          service_ids: finalTemplate.services?.map((s) => s.id) || [],
          sunbed_ids: finalTemplate.sunbeds?.map((s) => s.id) || [],
        };

        const res = await saveForm(finalTemplate.uuid, dataToSend);

        // Update local template state
        // CRITICAL FIX: Preserve section types from the local state if backend returns null
        const updatedSections = res.form?.sections?.map((serverSection: any) => {
          const originalSection = finalTemplate.sections.find(s => String(s.id) === String(serverSection.id));
          return {
            ...serverSection,
            type: serverSection.type || originalSection?.type || "textarea", // Fallback chain
            fields: serverSection.fields || [],
          };
        }) || finalTemplate.sections;

        updatedSections.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

        const updatedTemplate: FormTemplate = {
          ...finalTemplate,
          id: String(res.form?.id || finalTemplate.id),
          uuid: res.form?.uuid || finalTemplate.uuid,
          saved: true,
          draft: res.form?.status === "draft",
          sections: updatedSections,
        };

        onUpdateTemplate(updatedTemplate);
        showToast.success("Form saved successfully!");
      } catch (err: any) {
        console.error("Error saving template:", err);
        showToast.error(err.message || "Failed to save form!");
      } finally {
        setIsSaving(false);
      }
    },
    [onUpdateTemplate, showToast]
  );

  return { saveTemplate, isSaving, message, setMessage };
}
